import React from 'react'
import cn from 'classnames'
import { ArrowBigUp } from 'lucide-react'

import styles from './post-answer.module.scss'

// No outer header: the notification row or embedded post already supplies the
// author and time. The card owns the invitation copy and actions in both places.
export default function HostInviteCard({
    busyAction = null,
    inviterName,
    inviteeName = null,
    onAccept = undefined,
    onDecline = undefined,
    onLearnMore = undefined,
    onPrimary = undefined,
    onTurnOnExisting = undefined,
    sharePercent = 10,
    status = 'pending',
    teamName = null,
    useUsd = false,
    viewerIsInvitee = true,
}) {
    const isLive = status === 'live'
    const isAccepted = status === 'accepted'
    const terminalStatus = isLive
        ? teamName || 'Live'
        : isAccepted
        ? 'Accepted'
        : status === 'revoked'
        ? 'Revoked'
        : 'Declined'

    const learnMoreButton = (
        <button
            type="button"
            className={styles.hostInviteLearnMore}
            data-testid="host-invite-learn-more"
            onClick={onLearnMore}
        >
            How host rewards work
        </button>
    )

    const pendingActions = viewerIsInvitee ? (
        <div className={styles.hostInviteActions}>
            <button
                type="button"
                className={cn(styles.hostInviteButton, styles.hostInviteAcceptButton)}
                data-testid="host-invite-accept"
                aria-disabled={!!busyAction}
                disabled={!!busyAction}
                onClick={onAccept}
            >
                {busyAction === 'accept' ? 'Accepting...' : 'Accept invite'}
            </button>
            <button
                type="button"
                className={cn(styles.hostInviteButton, styles.hostInviteDeclineButton)}
                data-testid="host-invite-decline"
                aria-disabled={!!busyAction}
                disabled={!!busyAction}
                onClick={onDecline}
            >
                {busyAction === 'decline' ? 'Declining...' : 'Decline'}
            </button>
            {learnMoreButton}
        </div>
    ) : (
        <div className={styles.hostInviteTerminal}>
            <div className={styles.hostInviteTerminalMeta}>
                <div className={styles.hostInviteStatus} data-testid="host-invite-read-only">
                    {inviteeName ? `Pending for @${inviteeName}` : 'Invitation pending'}
                </div>
                {learnMoreButton}
            </div>
        </div>
    )

    const actions =
        status === 'pending' ? (
            pendingActions
        ) : (
            <div className={styles.hostInviteTerminal}>
                {/* Status and the explainer are both meta: they share a line,
                    and the two ways to start earning get a row of their own. */}
                <div className={styles.hostInviteTerminalMeta}>
                    <div className={styles.hostInviteStatus}>{terminalStatus}</div>
                    {learnMoreButton}
                </div>
                {viewerIsInvitee && (isLive || isAccepted) && (
                    <div className={cn(styles.hostInviteActions, styles.hostInviteTerminalActions)}>
                        <button
                            type="button"
                            className={cn(styles.hostInviteButton, styles.hostInviteAcceptButton)}
                            data-testid="host-invite-create-stream"
                            onClick={onPrimary}
                        >
                            Create your channel
                        </button>
                        {/* A host may already run streams; the picker switches
                            rewards on for one instead of starting empty. */}
                        <button
                            type="button"
                            className={cn(styles.hostInviteButton, styles.hostInviteDeclineButton)}
                            data-testid="host-invite-turn-on-existing"
                            onClick={onTurnOnExisting}
                        >
                            Turn On Existing Channel
                        </button>
                    </div>
                )}
            </div>
        )

    return (
        <div className={styles.streamInvitePanel}>
            <section
                className={cn(
                    styles.hostInviteCard,
                    useUsd ? styles.hostInviteUsd : styles.hostInviteBitcoin,
                )}
                data-testid="host-invite-card"
            >
                <div className={styles.hostInviteBody}>
                    <h3 className={styles.hostInviteTitle}>
                        <span>@{inviterName}</span> invited{' '}
                        {viewerIsInvitee ? 'you' : inviteeName ? `@${inviteeName}` : 'a host'} to
                        channel host revenue share
                    </h3>
                    <div className={styles.hostInviteTerms} data-testid="host-invite-terms">
                        <span className={styles.hostInviteTermsMark} aria-hidden="true">
                            <ArrowBigUp size={22} strokeWidth={2.5} />
                        </span>
                        <span className={styles.hostInviteTermsText}>
                            Participating hosts can collect{' '}
                            <strong>{sharePercent}% of the upvalue</strong> exchanged in their
                            channels by members
                        </span>
                    </div>
                    {actions}
                </div>
            </section>
        </div>
    )
}
